import React from "react";
import styles from "../../main/ObjectDetails/ObjectDetails.module.scss";
import AnimatedDetailsContainer from "../../../../share/ResultDetails/AnimatedDetailsContainer/AnimatedDetailsContainer";
import DetailsHeader from "../../../../share/ResultDetails/DetailsHeader/DetailsHeader";
import { CyberSecurityListItem } from "../../../../models/CybersecurityObject";
import { useTranslation } from "react-i18next";
import {
  getCybersecurityIcon,
  getDomainFromAddress,
} from "../../../../util/cyberSecurityUtil";
import { getWebsiteFavicon } from "../../../../util/faviconUtil";
import { useTypedSelector } from "../../../../hooks/useTypedSelector";
import { CustomLoader } from "../../../../share/Loader/Loader";
import { Check } from "../../../../constants/icons";

interface ListViewCybersecurityMapDetailsProps {
  company: CyberSecurityListItem;
  isDetailsActive: boolean;
  onClose: () => void;
}

const ListViewCybersecurityMapDetails: React.FC<
  ListViewCybersecurityMapDetailsProps
> = ({ company, isDetailsActive, onClose }) => {
  const { t: tDetails } = useTranslation("translation", {
    keyPrefix: "mainPage.mapFilters.objectDetails.details",
  });
  const { isLoading } = useTypedSelector((state) => state.auth);
  const { selectedObject: cybersecurityCompany } = useTypedSelector(
    (state) => state.mapCybersecurityInfo
  );

  const detailsSource =
    cybersecurityCompany ?? (company as Partial<CyberSecurityListItem>);

  const faviconSrc = getWebsiteFavicon(company?.website);
  const logoSrc = faviconSrc ?? getCybersecurityIcon(company);

  const description = company?.address || "";
  const title = company?.name || "";

  const linkedinValue = detailsSource?.linkedin_company?.trim();
  const websiteValue = detailsSource?.website?.trim();
  const emailValue = detailsSource?.email?.trim();
  const phoneValues = Array.isArray(detailsSource?.contacts)
    ? detailsSource?.contacts
        .map((contact) => contact?.trim())
        .filter((contact): contact is string => Boolean(contact?.length))
    : [];

  const getOtherServicesLabel = () => {
    const otherServices = detailsSource?.other_services ?? "";

    const cybersecurityPercentage = detailsSource?.cybersecurity_percentage;

    if (otherServices.toLowerCase() === "профільна") {
      return tDetails("Profile");
    }

    if (cybersecurityPercentage) {
      return tDetails("Percentage", { percentage: cybersecurityPercentage });
    }

    return otherServices || tDetails("undefined");
  };

  const servicesList =
    detailsSource && Array.isArray(detailsSource.services)
      ? detailsSource.services
      : [];

  const descriptionValue =
    detailsSource?.description && detailsSource.description.trim().length > 0
      ? detailsSource.description
      : tDetails("undefined");

  return (
    <AnimatedDetailsContainer isShown={isDetailsActive}>
      <DetailsHeader
        title={title}
        description={description}
        closeDetails={onClose}
        logo={
          logoSrc ? <img src={logoSrc} alt="" width={40} height={40} /> : null
        }
      />
      <div className={styles.listViewDetailsLayout}>
        {isLoading ? (
          <div className={styles.listViewPanels}>
            <section className={styles.listViewPanel}>
              <div className={styles.listViewPanelContent}>
                <CustomLoader />
              </div>
            </section>
          </div>
        ) : (
          <div className={styles.listViewPanels}>
            <section className={styles.listViewPanel}>
              <h3 className={styles.listViewPanelTitle}>
                {tDetails("companyCharacteristicTitle")}
              </h3>
              <div className={styles.listViewPanelContent}>
                <table className={styles.tabTable}>
                  <tbody>
                    <tr className={styles.tabRow}>
                      <td>{tDetails("edrpou")}:</td>
                      <td>{detailsSource?.edrpou ?? tDetails("undefined")}</td>
                    </tr>
                    <tr className={styles.tabRow}>
                      <td>{tDetails("entityType")}:</td>
                      <td>
                        {detailsSource?.company_type ?? tDetails("undefined")}
                      </td>
                    </tr>
                    <tr className={styles.tabRow}>
                      <td>{tDetails("companySize")}:</td>
                      <td>
                        {detailsSource?.company_size ?? tDetails("undefined")}
                      </td>
                    </tr>
                    <tr className={styles.tabRow}>
                      <td>{tDetails("registrationDate")}:</td>
                      <td>
                        {detailsSource?.registration_date ?? <span>-</span>}
                      </td>
                    </tr>
                    <tr className={styles.tabRow}>
                      <td>{tDetails("region")}:</td>
                      <td>{detailsSource?.region ?? tDetails("undefined")}</td>
                    </tr>
                    <tr className={styles.tabRow}>
                      <td>{tDetails("linkedin")}:</td>
                      <td>
                        {linkedinValue ? (
                          <a
                            className={styles.detailsLink}
                            href={linkedinValue}
                            target="_blank"
                            rel="noreferrer"
                            title={linkedinValue}
                          >
                            {linkedinValue}
                          </a>
                        ) : (
                          tDetails("undefined")
                        )}
                      </td>
                    </tr>
                    <tr className={styles.tabRow}>
                      <td>{tDetails("website")}:</td>
                      <td>
                        {websiteValue ? (
                          <a
                            className={styles.detailsLink}
                            href={
                              websiteValue.startsWith("http")
                                ? websiteValue
                                : `https://${websiteValue}`
                            }
                            target="_blank"
                            rel="noreferrer"
                            title={websiteValue}
                          >
                            {getDomainFromAddress(websiteValue)}
                          </a>
                        ) : (
                          tDetails("undefined")
                        )}
                      </td>
                    </tr>
                    <tr className={styles.tabRow}>
                      <td>{tDetails("email")}:</td>
                      <td>
                        {emailValue ? (
                          <a
                            className={styles.detailsLink}
                            href={`mailto:${emailValue}`}
                            target="_blank"
                            rel="noreferrer"
                            title={emailValue}
                          >
                            {emailValue}
                          </a>
                        ) : (
                          tDetails("undefined")
                        )}
                      </td>
                    </tr>
                    <tr className={styles.tabRow}>
                      <td>{tDetails("phone")}:</td>
                      <td>
                        {phoneValues.length > 0 ? (
                          <div className={styles.contactLinks}>
                            {phoneValues.map((phoneNumber, index) => {
                              const phoneHref = phoneNumber.replace(/\s+/g, "");

                              return (
                                <a
                                  key={`${phoneHref}-${index}`}
                                  className={styles.detailsLink}
                                  href={`tel:${phoneHref}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  title={phoneNumber}
                                >
                                  {phoneNumber}
                                </a>
                              );
                            })}
                          </div>
                        ) : (
                          tDetails("undefined")
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
            <section className={styles.listViewPanel}>
              <h3 className={styles.listViewPanelTitle}>
                {tDetails("servicesTitle")}
              </h3>
              <div className={styles.listViewPanelContent}>
                {servicesList.length > 0 ? (
                  <ul className={styles.servicesList}>
                    {servicesList.map((service, index) => (
                      <li
                        className={styles.serviceItem}
                        key={`${service}-${index}`}
                      >
                        <img
                          className={styles.serviceIcon}
                          src={Check}
                          alt=""
                        />
                        <span className={styles.serviceText}>{service}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className={styles.tabText}>
                    {tDetails("undefined")}
                  </span>
                )}
              </div>
            </section>
            <section className={styles.listViewPanel}>
              <h3 className={styles.listViewPanelTitle}>
                {tDetails("companyActivityTitle")}
              </h3>
              <div className={styles.listViewPanelContent}>
                <table className={styles.tabTable}>
                  <tbody>
                    <tr className={styles.tabRow}>
                      <td>{tDetails("certifications")}:</td>
                      <td>
                        {detailsSource?.certifications
                          ? tDetails("yes")
                          : tDetails("no")}
                      </td>
                    </tr>
                    <tr className={styles.tabRow}>
                      <td>{tDetails("other_services")}:</td>
                      <td>{getOtherServicesLabel()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className={styles.listViewPanel}>
              <h3 className={styles.listViewPanelTitle}>
                {tDetails("description")}
              </h3>
              <div className={styles.listViewPanelContent}>
                <p className={styles.sectionDescriptionText}>
                  {descriptionValue}
                </p>
              </div>
            </section>
          </div>
        )}
      </div>
    </AnimatedDetailsContainer>
  );
};

export default ListViewCybersecurityMapDetails;
